<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { describeError } from '../api/http'
import { useSession } from '../stores/session'
import AuthFrame from '../components/AuthFrame.vue'

const router = useRouter()
const session = useSession()
const userId = ref('')
const code = ref('')
const busy = ref(false)
const error = ref<string | null>(null)

async function recover(): Promise<void> {
  busy.value = true
  error.value = null
  try {
    await session.recoverAccount(userId.value.trim().toLowerCase(), code.value)
    await router.replace({ name: 'home' })
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AuthFrame testid="recover" caption="recovery">
      <h1 class="mb-2 text-3xl font-light tracking-tight">Recover your account</h1>
      <p class="mb-5 text-sm text-text-muted">
        Lost your device? Use the recovery code you saved. You’ll rejoin all your spaces and chats on this device;
        older messages stay on your old devices.
      </p>
      <form class="space-y-4" @submit.prevent="recover">
        <div>
          <label class="label" for="recover-user">Your ID</label>
          <input
            id="recover-user"
            v-model="userId"
            class="input mono"
            placeholder="name@your-hrth.example"
            autocomplete="username"
            data-testid="recover-user"
          />
        </div>
        <div>
          <label class="label" for="recover-code">Recovery code</label>
          <textarea
            id="recover-code"
            v-model="code"
            rows="2"
            class="input mono tracking-[0.1em] uppercase"
            placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
            autocomplete="off"
            spellcheck="false"
            data-testid="recover-code"
          />
        </div>
        <p v-if="error" class="border border-signal px-3 py-2 text-sm text-error" data-testid="recover-error">
          {{ error }}
        </p>
        <button class="btn-primary w-full" :disabled="busy || !userId.includes('@') || !code.trim()" data-testid="recover-submit">
          {{ busy ? 'Recovering…' : 'Recover account' }}
        </button>
        <button type="button" class="btn-ghost w-full" @click="router.push('/')">Back</button>
      </form>
  </AuthFrame>
</template>
